import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { t } from "i18next";
import {
  Button,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Card,
  CardBody,
  Input,
} from "reactstrap";
import { FormControlLabel, Switch } from "@mui/material";
import { useKitchenStore } from "../../../../stores/Assets/kitchen";
import { Link } from "react-router-dom";
import { useUsageStore } from "../../../../stores/Usage";

const AddKitchen = ({
  siteList,
  menuId,
  showAddKitchenModal,
  toggleAddKitchenModal,
}) => {
  const [openingHours, setOpeningHours] = useState([
    { dayOfWeek: "MONDAY", openTime: "", closeTime: "", timezone: "+00:00" },
  ]);
  const { createKitchen, isLoading } = useKitchenStore((state) => state);
  const [selectedSiteId, setSelectedSiteId] = useState(
    siteList[0]?.site_id || null
  );

  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showCustomPayment, setShowCustomPayment] = useState(false);
  const [customPayment, setCustomPayment] = useState({
    id: "",
    name: "",
    name_ar: "",
    icon: "cash",
  });

  // Suggested payment methods
  const suggestedMethods = [
    { id: "CASH", name: "Cash", name_ar: "نقدي", icon: "cash" },
    { id: "CARD", name: "Card", name_ar: "بطاقة", icon: "card" },
  ];

  const togglePaymentMethod = (method) => {
    const exists = paymentMethods.find((m) => m.id === method.id);
    if (exists) {
      setPaymentMethods(paymentMethods.filter((m) => m.id !== method.id));
    } else {
      setPaymentMethods([...paymentMethods, method]);
    }
  };

  const addCustomPaymentMethod = () => {
    if (!customPayment.name) return;

    // Auto-generate ID from name
    const generatedId = customPayment.name.toUpperCase().replace(/\s+/g, "_").replace(/[^A-Z0-9_]/g, "");

    setPaymentMethods([...paymentMethods, {
      ...customPayment,
      id: generatedId
    }]);
    setCustomPayment({ id: "", name: "", name_ar: "", icon: "cash" });
    setShowCustomPayment(false);
  };

  const removePaymentMethod = (methodId) => {
    setPaymentMethods(paymentMethods.filter((m) => m.id !== methodId));
  };
  const formik = useFormik({
    initialValues: {
      name: "",
      name_ar: "",
      isOpen: true,
      isWeeklyTimingOn: false,
    },
    validationSchema: Yup.object({
      name: Yup.string().required(t("Required")),
      name_ar: Yup.string().optional(),
      isOpen: Yup.boolean().required(t("Required")),
      isWeeklyTimingOn: Yup.boolean().required(t("Required")),
    }),
    onSubmit: async (values) => {
      // if (
      //   values.isWeeklyTimingOn &&
      //   openingHours.every(
      //     (hour) => !hour.dayOfWeek || !hour.openTime || !hour.closeTime
      //   )
      // ) {
      //   return; // Prevent submission if no opening hours are filled
      // }

      let kitchenData = {
        name: values.name,
        name_ar: values.name_ar,
        site_id: parseInt(selectedSiteId),
        isOpen: true,
        isWeeklyTimingOn: false,
        openingHours: values.isWeeklyTimingOn ? openingHours : [],
        payment_methods: paymentMethods.length > 0 ? paymentMethods : null,
      };


      const result = await createKitchen(kitchenData);
      if (result) {
        useUsageStore.getState().fetchUsage();
        formik.resetForm();
        setOpeningHours([
          {
            dayOfWeek: "MONDAY",
            openTime: "",
            closeTime: "",
            timezone: "+00:00",
          },
        ]); // Reset opening hours
        toggleAddKitchenModal();
      }
    },
  });

  // const addOpeningHour = () => {
  //   if (
  //     openingHours.some(
  //       (hour) => !hour.dayOfWeek || !hour.openTime || !hour.closeTime
  //     )
  //   ) {
  //     return; // Prevent adding a new opening hour if the current one is incomplete
  //   }
  //   setOpeningHours([
  //     ...openingHours,
  //     { dayOfWeek: "TUESDAY", openTime: "", closeTime: "", timezone: "+00:00" },
  //   ]);
  // };

  // const handleOpeningHourChange = (index, field, value) => {
  //   const newOpeningHours = [...openingHours];
  //   newOpeningHours[index][field] = value;
  //   setOpeningHours(newOpeningHours);
  // };

  // const removeOpeningHour = (index) => {
  //   const newOpeningHours = openingHours.filter((_, i) => i !== index);
  //   setOpeningHours(newOpeningHours);
  // };

  return (
    <Modal isOpen={showAddKitchenModal} toggle={toggleAddKitchenModal}>
      <ModalHeader toggle={toggleAddKitchenModal}>
        {t("Add New Kitchen")}
      </ModalHeader>
      <ModalBody>
        {siteList.length === 0 ? (
          <div className="text-center">
            <p className="text-danger">{t("You should first add a site.")}</p>
            <Link to="/sites" className="btn btn-primary">
              {t("Go to Sites")}
            </Link>
          </div>
        ) : (
          <form
            onSubmit={formik.handleSubmit}
            className="d-flex flex-column flex-wrap"
          >
            <div className="flex-fill mb-2">
              <Label for="name">{t("Name")}</Label>
              <input
                type="text"
                id="name"
                name="name"
                onChange={formik.handleChange}
                value={formik.values.name}
                className="form-control"
              />
              {formik.errors.name && (
                <div className="text-danger">{formik.errors.name}</div>
              )}
            </div>
            <div className="flex-fill mb-2">
              <Label for="name_ar">{t("Name (AR)")}</Label>
              <input
                type="text"
                id="name_ar"
                name="name_ar"
                onChange={formik.handleChange}
                value={formik.values.name_ar}
                className="form-control"
              />
              {formik.errors.name && (
                <div className="text-danger">{formik.errors.name_ar}</div>
              )}
            </div>
            <div className="flex-fill mb-2">
              <Label for="site_id">{t("Select Site")}</Label>
              <select
                id="site_id"
                onChange={(e) => setSelectedSiteId(e.target.value)}
                value={selectedSiteId}
                className="form-control"
              >
                {formik.errors.name && (
                  <div className="text-danger">{formik.errors.site_id}</div>
                )}
                {siteList.map((site) => (
                  <option key={site.site_id} value={site.site_id}>
                    {site.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Payment Methods Configuration */}
            <div className="flex-fill mb-3 mt-3">
              <Label>{t("Payment Methods (Optional)")}</Label>
              <p className="text-muted small mb-2">
                {t("Select payment methods for restaurant orders. Leave empty to skip payment selection.")}
              </p>

              {/* Suggested Payment Methods */}
              <div className="mb-3">
                <Label className="small text-muted">{t("Suggested Methods")}</Label>
                <div className="d-flex gap-2 flex-wrap">
                  {suggestedMethods.map((method) => {
                    const isSelected = paymentMethods.find((m) => m.id === method.id);
                    return (
                      <Button
                        key={method.id}
                        type="button"
                        color={isSelected ? "success" : "outline-secondary"}
                        size="sm"
                        onClick={() => togglePaymentMethod(method)}
                      >
                        <i className={`ri-${method.icon}-line me-1`}></i>
                        {method.name}
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Custom Payment Method */}
              <div className="mb-2">
                <Button
                  type="button"
                  color="info"
                  outline
                  size="sm"
                  onClick={() => setShowCustomPayment(!showCustomPayment)}
                >
                  <i className="ri-add-line me-1"></i>
                  {t("Add Custom Payment Method")}
                </Button>
              </div>

              {showCustomPayment && (
                <Card className="mb-3">
                  <CardBody>
                    <div className="mb-2">
                      <Label for="custom_name">{t("Name")} *</Label>
                      <Input
                        type="text"
                        id="custom_name"
                        value={customPayment.name}
                        onChange={(e) =>
                          setCustomPayment({ ...customPayment, name: e.target.value })
                        }
                        placeholder="Benefit Pay"
                      />
                      <small className="text-muted">
                        {t("ID will be auto-generated")}: {customPayment.name ? customPayment.name.toUpperCase().replace(/\s+/g, "_").replace(/[^A-Z0-9_]/g, "") : ""}
                      </small>
                    </div>
                    <div className="mb-2">
                      <Label for="custom_name_ar">{t("Name (AR)")}</Label>
                      <Input
                        type="text"
                        id="custom_name_ar"
                        value={customPayment.name_ar}
                        onChange={(e) =>
                          setCustomPayment({ ...customPayment, name_ar: e.target.value })
                        }
                        placeholder="بينفت"
                      />
                    </div>
                    <div className="mb-2">
                      <Label for="custom_icon">{t("Icon")}</Label>
                      <select
                        id="custom_icon"
                        value={customPayment.icon}
                        onChange={(e) =>
                          setCustomPayment({ ...customPayment, icon: e.target.value })
                        }
                        className="form-control"
                      >
                        <option value="cash">Cash</option>
                        <option value="bank-card">Card</option>
                        <option value="wallet">Wallet</option>
                        <option value="smartphone">Mobile</option>
                        <option value="bank">Bank</option>
                      </select>
                    </div>
                    <Button
                      type="button"
                      color="success"
                      size="sm"
                      onClick={addCustomPaymentMethod}
                      disabled={!customPayment.name}
                    >
                      {t("Add")}
                    </Button>
                  </CardBody>
                </Card>
              )}

              {/* Selected Payment Methods */}
              {paymentMethods.length > 0 && (
                <div>
                  <Label className="small text-muted">{t("Active Payment Methods")}</Label>
                  <div className="d-flex gap-2 flex-wrap">
                    {paymentMethods.map((method) => (
                      <div
                        key={method.id}
                        className="badge bg-primary d-flex align-items-center gap-1 p-2"
                      >
                        <i className={`ri-${method.icon}-line`}></i>
                        <span>{method.name}</span>
                        <button
                          type="button"
                          className="btn-close btn-close-white"
                          style={{ fontSize: "0.6rem" }}
                          onClick={() => removePaymentMethod(method.id)}
                          aria-label="Remove"
                        ></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* <FormControlLabel
            control={
              <Switch
                checked={formik.values.isOpen}
                onChange={(event) =>
                  formik.setFieldValue("isOpen", event.target.checked)
                }
              />
            }
            label={t("Is Open")}
          />

          <FormControlLabel
            control={
              <Switch
                checked={formik.values.isWeeklyTimingOn}
                onChange={(event) =>
                  formik.setFieldValue("isWeeklyTimingOn", event.target.checked)
                }
              />
            }
            label={t("Apply Weekly Opening Time")}
          />

          {formik.values.isWeeklyTimingOn && (
            <>
              <Label className="mt-3">{t("Opening Hours")}</Label>
              {openingHours.map((hour, index) => (
                <div key={index} className="d-flex mb-2 align-items-center">
                  <select
                    value={hour.dayOfWeek}
                    onChange={(e) =>
                      handleOpeningHourChange(
                        index,
                        "dayOfWeek",
                        e.target.value
                      )
                    }
                    className="form-control me-2"
                  >
                    {[
                      "MONDAY",
                      "TUESDAY",
                      "WEDNESDAY",
                      "THURSDAY",
                      "FRIDAY",
                      "SATURDAY",
                      "SUNDAY",
                    ].map((day) => (
                      <option key={day} value={day}>
                        {t(day)}
                      </option>
                    ))}
                  </select>
                  <input
                    type="time"
                    value={hour.openTime}
                    onChange={(e) =>
                      handleOpeningHourChange(index, "openTime", e.target.value)
                    }
                    className="form-control me-2"
                    required
                  />
                  <input
                    type="time"
                    value={hour.closeTime}
                    onChange={(e) =>
                      handleOpeningHourChange(
                        index,
                        "closeTime",
                        e.target.value
                      )
                    }
                    className="form-control me-2"
                    required
                  />
                  <Button
                    color="danger"
                    outline
                    onClick={() => removeOpeningHour(index)}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                color="primary"
                outline
                onClick={addOpeningHour}
                disabled={openingHours.some(
                  (hour) => !hour.dayOfWeek || !hour.openTime || !hour.closeTime
                )}
              >
                Add Opening Hour
              </Button>
            </>
          )} */}

            <div className="d-flex flex-row align-items-center justify-content-center gap-2 mt-2">
              <Button
                type="submit"
                color="success"
                disabled={
                  isLoading ||
                  (formik.values.isWeeklyTimingOn &&
                    openingHours.every(
                      (hour) =>
                        !hour.dayOfWeek || !hour.openTime || !hour.closeTime
                    ))
                }
              >
                {isLoading ? (
                  <Spinner size="sm" />
                ) : (
                  <span>{t("Add Kitchen")}</span>
                )}
              </Button>
              <Button
                color="danger"
                onClick={() => {
                  toggleAddKitchenModal();
                  formik.resetForm();
                  setOpeningHours([
                    {
                      dayOfWeek: "MONDAY",
                      openTime: "",
                      closeTime: "",
                      timezone: "+00:00",
                    },
                  ]);
                }}
                disabled={isLoading}
              >
                {t("Cancel")}
              </Button>
            </div>
          </form>
        )}
      </ModalBody>
      <ModalFooter>{/* Additional footer content can go here */}</ModalFooter>
    </Modal>
  );
};

export default AddKitchen;
